/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * RecordDetailsPage
 * -----------------
 * This page displays detailed information of a single record based on the record ID extracted from the URL.
 *
 * Responsibilities:
 *  - Fetches the record data from the backend API by sending a POST request to a record-specific endpoint.
 *  - Receives the table schema via navigation state to map raw field keys to user-friendly display names.
 *  - Handles loading states by showing a progress bar component during data fetch.
 *  - Handles error scenarios by displaying an alert dialog with error messages and provides navigation back to the table view page.
 *  - Renders the record details in a two-column grid format with uniform spacing and labeling.
 *  - Excludes certain fields such as the primary key ("id") from the display.
 *
 * Key integrations:
 *  - Uses APIHelper for backend API communication with credentials and environment-configured URLs.
 *  - Uses custom UI components: ProgressBarDialog and AlertDialog for user feedback.
 *  - Uses React Router hooks: useParams to parse URL parameters, useNavigate to enable programmatic navigation,
 *    and useLocation to access passed navigation state (table schema).
 *  - Uses ThemeContext for dynamic theming of UI elements.
 *  - Applies CSS styles from an external stylesheet for layout and visual consistency.
 */

import React, { useMemo, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import envConfig from '../config/envConfig';
import APIHelper from '../api/APIHelper';
import { useTheme } from '../theme/ThemeContext';
import '../styles/RecordsDetailPage.css';
import ProgressBarDialog from '../components/common/ProgressBar';
import AlertDialog from '../components/common/AlertDialog';
import { convertUTCStringToLocalStandard, toDisplayString } from "../utils/utils";
import { retrieve } from '../utils/storage_helper';
import ImageComponent from "../components/common/ImageComponent";
import { useQuery } from '@tanstack/react-query';
import { useBreadcrumbNames } from '../context/BreadCrumbNameContext';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

export function RecordDetailsCard({ data, tableSchema }) {
    if (!data) return null;

    // Map field name -> display name
    const displayNameMap = {};
    tableSchema?.fields?.forEach(field => {
        displayNameMap[field.name] = field.display_name || field.name;
    });

    const pairs = Object.entries(data || {})
        .filter(([key]) => key !== "id" && key !== "created_by_id")
        .map(([key, value]) => {
            const field = tableSchema?.fields?.find(f => f.name === key);

            if (value && typeof value === "object" && value.data) {
                const identifier = value.record_identifier || "id";
                const displayValue = value.data?.[identifier] || value.data?.id || "-";
                return [key, displayValue];
            }

            if (field?.data_type === "datetime" && value) {
                return [key, convertUTCStringToLocalStandard(value)];
            }

            if (value === null || value === undefined || value === "") {
                return [key, "-"];
            }

            // Any object value (geo_point, json, etc.) -> safe string, never "[object Object]"
            if (typeof value === "object") {
                return [key, toDisplayString(value)];
            }

            return [key, value];
        });

    const rows = [];
    for (let i = 0; i < pairs.length; i += 2) {
        rows.push(pairs.slice(i, i + 2));
    }

    return (
        <div className="record-details-card">
            {rows.map((row, rowIdx) => (
                <div className="record-details-row" key={rowIdx}>
                    {row.map(([key, value], colIdx) => {
                        const fieldMeta = tableSchema?.fields?.find(f => f.name === key);
                        const imageUrl = typeof value === "object" && value?.props?.src ? value.props.src : value;
                        const displayLabel = displayNameMap[key] || key;

                        let uiRules = fieldMeta?.ui_rules || {};
                        if (typeof uiRules === 'string') {
                            try { uiRules = JSON.parse(uiRules); } catch (e) { uiRules = {}; }
                        }
                        const badgeSettings = uiRules?.display_as_badge;
                        const isBadge = badgeSettings?.enabled === true && value !== "-" && value !== null && value !== undefined;

                        return (
                            <div className="key-value-pair" key={key || colIdx}>
                                <span className="key-label">{displayLabel}</span>
                                <span className="kv-sep">:</span>
                                <span className="kv-value">
                                    {fieldMeta?.data_type?.toLowerCase() === "image" ? (
                                        <ImageComponent src={imageUrl} alt={displayLabel} height={140} shape="square" />
                                    ) : (
                                        isBadge ? (
                                            <span
                                                style={{
                                                    backgroundColor: badgeSettings.color || "#0d6efd",
                                                    color: "#fff",
                                                    padding: "4px 8px",
                                                    borderRadius: "4px",
                                                    fontSize: "0.9rem",
                                                    fontWeight: "500",
                                                    display: "inline-block"
                                                }}
                                            >
                                                {value.toString()}
                                            </span>
                                        ) : (
                                            (value !== null && value !== undefined) ? value.toString() : "-"
                                        )
                                    )}
                                </span>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}

export default function RecordDetailsPage() {
    const { tableName, recordId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { theme } = useTheme();
    const tableSchemaFromState = location.state?.tableSchema;
    const { setTableDisplayNames } = useBreadcrumbNames();

    const queryKey = useMemo(() => [
        "recordDetail",
        tableName,
        recordId,
        envConfig.organizationId,
        envConfig.applicationId,
    ], [tableName, recordId]);

    // query function forwards signal for cancellation
    const fetchRecord = async ({ signal }) => {
        if (!recordId || !tableName) return null;

        const response = await apiHelper.apiCall(`/arcee/application/table/fetch/${recordId}/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                table_name: tableName,
                organization_id: envConfig.organizationId,
                application_id: envConfig.applicationId
            }),
            signal,
        });

        if (!response.ok) {
            let errText = `Failed to fetch record (${response.status})`;
            try {
                const j = await response.json();
                if (j && j.error) errText = j.error;
            } catch (e) { }
            throw new Error(errText);
        }

        const data = await response.json();
        return data;
    };

    const {
        data: recordData,
        error,
        isLoading,
        isFetching,
        refetch,
    } = useQuery({
        queryKey,
        queryFn: fetchRecord,
        enabled: !!recordId && !!tableName,
        staleTime: envConfig.staleTimeMs,
        retry: 1,
    });

    useEffect(() => {
        const schema = tableSchemaFromState || recordData?.table_schema;
        if (schema?.table_name) {
            setTableDisplayNames(prev => ({
                ...prev,
                [schema.table_name]: schema.display_name || schema.table_name,
            }));
        }
    }, [tableSchemaFromState, recordData, setTableDisplayNames]);

    return (
        <>
            <ProgressBarDialog open={isLoading || isFetching} text_message="Loading record details..." />

            <AlertDialog
                open={!!error}
                title="Error"
                message={error?.message || error}
                type="error"
                okLabel="Close"
                showCancel={false}
                onOk={() => {
                    // navigate back to table view
                    navigate(`/dashboard/tables/${tableName}`);
                }}
            />

            {!isLoading && !error && recordData && (
                <>
                    <h4 style={{ color: theme.primary, marginBottom: 12 }}>
                        {tableSchemaFromState?.display_name || recordData?.table_schema?.display_name || tableName} - Details
                    </h4>
                    <RecordDetailsCard data={recordData} tableSchema={tableSchemaFromState || recordData?.table_schema} />
                </>
            )}

            {!isLoading && !error && !recordData && (
                <div style={{ padding: 12, color: '#666' }}>No record found.</div>
            )}
        </>
    );
}
